import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { useData } from './DataProvider';
import { safeParseJson } from '@appesteem/aeshared-js/loadwatch';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import defaultFilters from './defaultFilters.json' with { type: 'json' };

const filterOps = [
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'contains', label: 'Contains' },
  { value: 'regex', label: 'Regex' },
];

function EditFiltersPopup({ open, onClose, columns, filters, addFilter, removeFilter, clearFilters }) {
  const [col, setCol] = useState(columns[0]?.id || '');
  const [op, setOp] = useState('equals');
  const [val, setVal] = useState('');
  const [mode, setMode] = useState('include');
  const [importMessage, setImportMessage] = useState('');
  const [importMessageType, setImportMessageType] = useState(''); // 'success' or 'error'
  const [editingIndex, setEditingIndex] = useState(-1); // Index of filter being edited, -1 means none

  // Clear import message when dialog opens
  React.useEffect(() => {
    if (open) {
      setImportMessage('');
      setImportMessageType('');
      setEditingIndex(-1);
      // Reset form
      setCol(columns[0]?.id || '');
      setOp('equals');
      setVal('');
      setMode('include');
    }
  }, [open, columns]);

  const handleAdd = () => {
    addFilter({ column: col, op, value: val, mode });
    setVal('');
  };

  const handleEditFilter = (index) => {
    const filter = filters[index];
    setCol(filter.column);
    setOp(filter.op);
    setVal(filter.value);
    setMode(filter.mode);
    setEditingIndex(index);
  };

  const handleSaveEdit = () => {
    // Remove the old filter and add the updated one
    removeFilter(editingIndex);
    addFilter({ column: col, op, value: val, mode });
    
    // Reset form and editing state
    setCol(columns[0]?.id || '');
    setOp('equals');
    setVal('');
    setMode('include');
    setEditingIndex(-1);
  };

  const handleCancelEdit = () => {
    // Reset form and editing state
    setCol(columns[0]?.id || '');
    setOp('equals');
    setVal('');
    setMode('include');
    setEditingIndex(-1);
  };

  const addDisabled = col === '' || op === '' || mode === '' || (val === '' && (op !== 'equals' || col === '__all_fields__')) || (col === '__all_fields__' && val === '');

  // Export filters to JSON file
  const handleExportFilters = async () => {
    const dataStr = JSON.stringify(filters, null, 2);
    
    // Check if the File System Access API is supported
    if ('showSaveFilePicker' in window) {
      try {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: 'filters.json',
          types: [
            {
              description: 'JSON files',
              accept: {
                'application/json': ['.json'],
              },
            },
          ],
        });
        
        const writable = await fileHandle.createWritable();
        await writable.write(dataStr);
        await writable.close();
      } catch (err) {
        // User cancelled the dialog or there was an error
        if (err.name !== 'AbortError') {
          console.error('Error saving file:', err);
          // Fallback to download method
          fallbackDownload(dataStr);
        }
      }
    } else {
      // Fallback for browsers that don't support File System Access API
      fallbackDownload(dataStr);
    }
  };

  // Fallback download method for older browsers
  const fallbackDownload = (dataStr) => {
    const filename = 'filters.json';
    
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Import filters from JSON file
  const handleImportFilters = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedFilters = safeParseJson(e.target.result);
        if (importedFilters === null) {
          setImportMessage('Error parsing JSON file. Please ensure it contains valid JSON.');
          setImportMessageType('error');
          return;
        }

        // Validate that it's an array of filter objects
        if (!Array.isArray(importedFilters)) {
          setImportMessage('Invalid file format. Expected an array of filters.');
          setImportMessageType('error');
          return;
        }

        // Validate each filter has required properties
        const isValid = importedFilters.every(filter =>
          filter.hasOwnProperty('column') &&
          filter.hasOwnProperty('op') &&
          filter.hasOwnProperty('value') &&
          filter.hasOwnProperty('mode')
        );

        if (!isValid) {
          setImportMessage('Invalid filter format. Each filter must have column, op, value, and mode properties.');
          setImportMessageType('error');
          return;
        }

        // Clear existing filters and add imported ones
        clearFilters();
        importedFilters.forEach(filter => {
          addFilter(filter);
        });

        // Clear any existing error messages on successful import
        setImportMessage('');
        setImportMessageType('');
      } catch (error) {
        setImportMessage('Error importing filters. Please ensure the file is well-formed.');
        setImportMessageType('error');
      }
    };
    reader.readAsText(file);
    
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Filters</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          {filters.length === 0 && <Typography variant="body2" color="text.secondary">No filters applied.</Typography>}
          <Box display="flex" flexDirection="column" gap={1} sx={{ maxHeight: 260, overflowY: 'auto' }}>
            {filters.map((f, i) => (
              <Box key={i} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                bgcolor: editingIndex === i ? 'action.selected' : 'background.default', 
                borderRadius: 1, 
                px: 1, 
                py: 1,
                border: editingIndex === i ? '2px solid' : 'none',
                borderColor: editingIndex === i ? 'primary.main' : 'transparent'
              }}>
                {f.mode === 'exclude'
                  ? <BlockIcon fontSize="small" sx={{ color: '#e57373', mr: 0.5 }} />
                  : <CheckCircleIcon fontSize="small" sx={{ color: '#43a047', mr: 0.5 }} />}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.column === '__all_fields__' 
                    ? `Search All Fields ${filterOps.find(o => o.value === f.op)?.label || f.op} "${f.value}"`
                    : `${columns.find(c => c.id === f.column)?.label || f.column} ${filterOps.find(o => o.value === f.op)?.label || f.op} "${f.value}"`
                  }
                </span>
                {editingIndex !== i && (
                  <>
                    <IconButton size="small" onClick={() => handleEditFilter(i)} sx={{ mr: 0.5 }} title="Edit Filter">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeFilter(i)} title="Delete Filter">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
                {editingIndex === i && (
                  <Typography variant="caption" sx={{ mr: 1, fontStyle: 'italic' }}>
                    Editing below...
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        
        {/* Import message display */}
        {importMessage && (
          <Box 
            mt={2} 
            mb={3}
            p={1} 
            borderRadius={1} 
            bgcolor={importMessageType === 'success' ? 'success.light' : 'error.light'}
            color={importMessageType === 'success' ? 'success.contrastText' : 'error.contrastText'}
          >
            <Typography variant="body2">{importMessage}</Typography>
          </Box>
        )}
        
        <Typography variant="subtitle2" mb={1}>
          {editingIndex >= 0 ? 'Edit Filter' : 'Add Filter'}
        </Typography>
        <Box display="flex" gap={1} mb={2} alignItems="flex-start">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Column</InputLabel>
            <Select value={col} onChange={e => setCol(e.target.value)} label="Column">
              {columns.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Operation</InputLabel>
            <Select value={op} onChange={e => setOp(e.target.value)} label="Operation">
              {filterOps.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            value={val}
            onChange={e => setVal(e.target.value)}
            placeholder={
              col === '__all_fields__' 
                ? "Search term (required)" 
                : op === 'regex' 
                  ? "Regex pattern (case insensitive)"
                  : "Value (blank allowed for equals)"
            }
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Mode</InputLabel>
            <Select value={mode} onChange={e => setMode(e.target.value)} label="Mode">
              <MenuItem value="include">Include</MenuItem>
              <MenuItem value="exclude">Exclude</MenuItem>
            </Select>
          </FormControl>
          {editingIndex >= 0 ? (
            <>
              <Button variant="contained" size="small" onClick={handleSaveEdit} disabled={addDisabled}>Save</Button>
              <Button variant="outlined" size="small" onClick={handleCancelEdit}>Cancel</Button>
            </>
          ) : (
            <Button variant="contained" size="small" onClick={handleAdd} disabled={addDisabled}>Add</Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} mr="auto" pl={2} pb={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportFilters}
            disabled={filters.length === 0}
            size="small"
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            component="label"
            size="small"
          >
            Import
            <input
              type="file"
              accept=".json"
              onChange={handleImportFilters}
              style={{ display: 'none' }}
            />
          </Button>
        </Box>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={clearFilters} color="error">Clear Filters</Button>
      </DialogActions>
    </Dialog>
  );
}

// Use the same columns as the event view (full set)
const columns = [
  { id: '__all_fields__', label: 'Search All Fields' }, // Special column for global search
  { id: 'Action', label: 'Action' },
  // { id: 'ActionTime', label: 'Time' },
  { id: 'Details', label: 'Details' },
  { id: 'EventType', label: 'EventType' },
  { id: 'Extra', label: 'Extra' },
  { id: 'ItemName', label: 'Item Name' }, // changed from Key/Path to ItemName
  { id: 'NewValue', label: 'New Value' },
  { id: 'OldValue', label: 'Old Value' },
  { id: 'Path', label: 'Path' },
  { id: 'Pid', label: 'PID' },
  { id: 'ProcessName', label: 'Process' },
  { id: 'SessionId', label: 'Session ID' },
  { id: 'Tid', label: 'TID' },
  { id: 'User', label: 'User' },
  { id: 'Value', label: 'Value' },
  { id: 'VideoOffset', label: 'Video Offset' },
  { id: 'WindowClass', label: 'Window Class' },
  { id: 'WindowHwnd', label: 'Window HWND' },
  { id: 'WindowTitle', label: 'Window Title' },
];

export default function FiltersBar() {
  const { filters, addFilter, removeFilter, clearFilters, platform } = useData();
  const [editPopupOpen, setEditPopupOpen] = useState(false);
  // Window Class / HWND are HWND-world fields the macOS collector never fills; don't offer them.
  const visibleColumns = useMemo(
    () => (platform === 'macos' ? columns.filter((c) => c.id !== 'WindowClass' && c.id !== 'WindowHwnd') : columns),
    [platform],
  );
  const didInit = React.useRef(false);

  // Set default filters only once on mount
  React.useEffect(() => {
    if (!didInit.current && filters.length === 0 && defaultFilters.length > 0) {
      defaultFilters.forEach(f => addFilter(f));
      didInit.current = true;
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Box display="flex" alignItems="center" gap={0.75} ml={0.5} mb={0.5} flexWrap="wrap">
      <EditFiltersPopup open={editPopupOpen} onClose={() => setEditPopupOpen(false)} columns={visibleColumns} filters={filters} addFilter={addFilter} removeFilter={removeFilter} clearFilters={clearFilters} />
      {filters.length > 0 && (
        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ rowGap: 0.5, flex: 1, minWidth: 0 }}>
          <Box display="flex" flexWrap="wrap" gap={0.75} sx={{ flex: 1, minWidth: 0 }}>
            {filters.map((f, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', fontSize: 12, px: 0.75, py: 0.25, borderRadius: 0.5,
                bgcolor: 'background.default',
                color: 'inherit',
                minWidth: 0,
                maxWidth: 320,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
                {f.mode === 'exclude'
                  ? <BlockIcon fontSize="small" sx={{ color: '#e57373', mr: 0.5, verticalAlign: 'middle' }} />
                  : <CheckCircleIcon fontSize="small" sx={{ color: '#43a047', mr: 0.5, verticalAlign: 'middle' }} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {f.column === '__all_fields__' 
                    ? `Search All ${filterOps.find(o => o.value === f.op)?.label || f.op} "${f.value}"`
                    : `${columns.find(c => c.id === f.column)?.label || f.column} ${filterOps.find(o => o.value === f.op)?.label || f.op} "${f.value}"`
                  }
                </span>
                <Button size="small" onClick={() => removeFilter(i)} sx={{ minWidth: 0, ml: 0.5, fontSize: 11, py: 0 }}>✕</Button>
              </Box>
            ))}
            {/* Edit Filters IconButton placed right after filters */}
            <IconButton size="small" color='primary' onClick={() => setEditPopupOpen(true)} title="Edit Filters" sx={{ p: 0.125, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
              <FilterListIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      )}
      {/* If no filters, show compact button */}
      {filters.length === 0 && (
        <Button 
          variant="outlined"
          size="small"
          startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
          onClick={() => setEditPopupOpen(true)} 
          sx={{ py: 0, px: 1, minHeight: 22, fontSize: 11, mr: 1 }}
        >
          Filters
        </Button>
      )}
    </Box>
  );
}
